import type { Pool, PoolKeyMap } from "./Pool";

/**
 * Base Pool interface implementation.
 */
export class BasePool<T = unknown, M extends PoolKeyMap<T> = PoolKeyMap<T>>
  implements Pool<T, M>
{
  /**
   * Pool items storage.
   * @private
   */
  private readonly storage: Map<keyof M | string, T>;

  /**
   * BasePool constructor.
   */
  constructor() {
    this.storage = new Map();
  }

  /**
   * @inheritDoc
   */
  has(key: keyof M | string): boolean {
    return this.storage.has(key);
  }

  /**
   * @inheritDoc
   */
  get<K extends keyof M | string>(key: K): K extends keyof M ? M[K] : T {
    const item = this.storage.get(key);
    if (item === undefined) {
      throw new RangeError(
        `Item with key "${key as string}" was not found in pool.`
      );
    }
    return item as K extends keyof M ? M[K] : T;
  }

  /**
   * @inheritDoc
   */
  add<K extends keyof M | string>(
    key: K,
    item: K extends keyof M ? M[K] : T,
    replace = false
  ): void {
    if (this.storage.has(key) && !replace) {
      throw new TypeError(
        `Item with key "${key as string}" already exists in pool`
      );
    }
    this.storage.set(key, item);
  }

  /**
   * @inheritDoc
   */
  remove(key: keyof M | string): void {
    this.storage.delete(key);
  }

  /**
   * @inheritDoc
   */
  keys(): (keyof M | string)[] {
    return Array.from(this.storage.keys());
  }

  /**
   * @inheritDoc
   */
  items(): T[] {
    return Array.from(this.storage.values());
  }
}
